import { useQueryParameter } from '@/context/QueryParameter';
import {
  ReceivedInvoiceOperationStatus,
  ReceivedInvoiceReconciliationStatus,
  TReceivedInvoiceCondition,
  mpReceivedInvoiceOperationStatus,
  mpReconciliationStatus,
} from '@/context/services/invoice/type';
import { DEFAULT_RECEIVED_INVOICE_OPERATION_STATUS_IN } from '@/features/invoices/components/List/Search/helper';
import { useReconciliationStatusVisible } from '@/features/invoices/hooks/useReconciliationStatusVisible';
import {
  hasExplicitSearch,
  isEmptyListUrl,
  resolveListSearch,
  saveLastListSearch,
} from '@/features/invoices/utils/listSearchStorage';
import { useRenewalUiReceivedInvoicesFlag } from '@/hooks/feature_flag/useRenewalUiReceivedInvoicesFlag';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useTranslation } from '@/i18n';
import { TableEmptyType } from '@moneyforward/ap-frontend-components';
import parser from 'html-react-parser';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const RECONCILIATION_STATUSES: string[] = Object.values(mpReconciliationStatus);
const OPERATION_STATUSES: string[] = Object.values(
  mpReceivedInvoiceOperationStatus
);

export const useReceivedInvoicesFormInitial = (): TReceivedInvoiceCondition => {
  const location = useLocation();
  const [, setParam] = useQueryParameter();
  const { getArrayParam, getDateParam } = useQueryParams();
  const isReconciliationStatusVisible = useReconciliationStatusVisible();
  const renewalUiReceivedInvoices = useRenewalUiReceivedInvoicesFlag();

  const locationSearch = location.search;
  const isUrlBlank = isEmptyListUrl(locationSearch);
  const [initialSearch] = useState(() =>
    renewalUiReceivedInvoices
      ? resolveListSearch(locationSearch)
      : locationSearch
  );
  const search = isUrlBlank ? initialSearch : locationSearch;

  const restoredRef = useRef(false);
  useEffect(() => {
    if (!renewalUiReceivedInvoices) return;
    if (restoredRef.current) return;
    restoredRef.current = true;
    if (hasExplicitSearch(locationSearch)) {
      saveLastListSearch(locationSearch);
    }
    if (isUrlBlank && search !== '') {
      setParam(new URLSearchParams(search), { replace: true });
    }
  }, [renewalUiReceivedInvoices, isUrlBlank, search, locationSearch, setParam]);

  return useMemo(() => {
    const searchParams = new URLSearchParams(search);
    const page = Number(searchParams.get('page')) || 1;

    // Array fields must fall back to [] — never undefined. Search.tsx resets the
    // form with these values, and react-hook-form treats an undefined value as
    // "missing" and restores the mount-time default, re-adding options the user
    // just cleared (NA-5938).
    return {
      ap_payee_id_in: getArrayParam(searchParams, 'ap_payee_id_in') ?? [],
      searchable_number_or_filename_cont:
        searchParams.get('searchable_number_or_filename_cont') || '',
      operation_status_in:
        getArrayParam(searchParams, 'operation_status_in') ?? [],
      pic_id_in: getArrayParam(searchParams, 'pic_id_in') ?? [],
      upload_type_in: getArrayParam(searchParams, 'upload_type_in') ?? [],
      uploaded_date: [
        getDateParam(searchParams, 'uploaded_at_gteq'),
        getDateParam(searchParams, 'uploaded_at_lteq'),
      ],
      due_date: [
        getDateParam(searchParams, 'due_date_from'),
        getDateParam(searchParams, 'due_date_to'),
      ],
      total_amount_from: searchParams.get('total_amount_from') || '',
      total_amount_to: searchParams.get('total_amount_to') || '',

      // Overrides `operation_status_in` above.
      // Be sure not to leave duplicate definitions when deleting the feature flag.
      ...(renewalUiReceivedInvoices
        ? {
            operation_status_in: (
              getArrayParam(searchParams, 'operation_status_in') ??
              DEFAULT_RECEIVED_INVOICE_OPERATION_STATUS_IN
            ).filter((value): value is ReceivedInvoiceOperationStatus =>
              OPERATION_STATUSES.includes(value)
            ),
          }
        : {}),

      ...(isReconciliationStatusVisible
        ? {
            reconciliation_status_in: (
              getArrayParam(searchParams, 'reconciliation_status_in') ?? []
            ).filter((value): value is ReceivedInvoiceReconciliationStatus =>
              RECONCILIATION_STATUSES.includes(value)
            ),
          }
        : {}),
      page,
    };
  }, [
    search,
    getArrayParam,
    getDateParam,
    isReconciliationStatusVisible,
    renewalUiReceivedInvoices,
  ]);
};

export const useEmptyState = (isSearchEmpty = false) => {
  const { t } = useTranslation();
  const isOnline = useNetworkStatus();
  const [emptyType, setEmptyType] = useState<TableEmptyType | undefined>(
    undefined
  );
  const [emptyMessage, setEmptyMessage] = useState<ReactNode | undefined>(
    undefined
  );
  useEffect(() => {
    if (!isOnline) {
      setEmptyType('error');
      setEmptyMessage(parser(t('system_error_network')));
    } else if (isSearchEmpty) {
      setEmptyType('search');
      setEmptyMessage(t('invoice_list_notfound_message'));
    } else {
      setEmptyType('file');
      setEmptyMessage(t('invoice_list_empty_message'));
    }
  }, [t, isOnline, isSearchEmpty]);

  return {
    emptyType,
    emptyMessage,
  };
};
